import billingPolicy from '../config/billingPolicy.json';
/**
 * AdminSystemSettings.js
 *
 * Admin UI for system-wide settings including cache management
 * and global feature flags (three-layer gate — system layer).
 * Flags are template-driven via featureFlagDefinitions.
 * See: docs/features/admin/admin-system-tab.md
 */

import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/apiUtils';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Chip,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, getDoc, setDoc, collection, query, where, Timestamp, getCountFromServer, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/authContext';
import { updateUserSettings } from '../utils/firestoreService';
import { systemGatedFlags } from '../config/featureFlagDefinitions';
import { appEnv, versionNum } from '../globals';
import AdminTemplates from './AdminTemplates';
import AdminDocumentTypeTemplates from './AdminDocumentTypeTemplates';

// Build a default flags object from the template (all system-gated flags start false)
const defaultSystemFlags = Object.fromEntries(
  systemGatedFlags.map((f) => [f.key, false]),
);

// Billing knobs — wired to Firestore (gracePeriodDays / daysToUnlock state).
const BILLING_SETTINGS = [
  { key: 'gracePeriodDays', label: 'Grace Period Days', description: 'Days after payment failure before access is revoked.', control: 'slider', min: 0, max: 30, step: 1, unit: '' },
  { key: 'daysToUnlock', label: 'Days to Unlock', description: 'Trial period for new users before requiring payment.', control: 'slider', min: 0, max: 90, step: 1, unit: '' },
];

const TRANSCRIPT_RETENTION_OPTIONS = [60, 90, 120];

/** Detect environment from runtime configuration (with hostname fallback) */
const getEnvironmentLabel = () => {
  if (appEnv === 'local' || appEnv === 'dev') return 'Development';
  if (appEnv === 'staging') return 'Staging';
  if (appEnv === 'production') return 'Production';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('dev.')) return 'Development';
  if (host.includes('staging') || host.includes('test')) return 'Staging';
  return 'Production';
};

const AdminSystemSettings = ({ adminPrefs, setAdminPrefs, userId, onDirtyChange }) => {
  const { currentUser } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState(adminPrefs?.systemSubTab ?? 0);
  const [hasUnsavedTemplateChanges, setHasUnsavedTemplateChanges] = useState(false);
  useEffect(() => { onDirtyChange?.(hasUnsavedTemplateChanges); }, [hasUnsavedTemplateChanges, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [flagSaveSuccess, setFlagSaveSuccess] = useState(false);
  const [isLoadingFlags, setIsLoadingFlags] = useState(true);
  const [isSavingFlags, setIsSavingFlags] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({ ...defaultSystemFlags });
  // Snapshot of flags as loaded from Firestore — used to detect which flags are being disabled.
  const [savedFeatureFlags, setSavedFeatureFlags] = useState({ ...defaultSystemFlags });
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [gracePeriodDays, setGracePeriodDays] = useState(7);
  const [daysToUnlock, setDaysToUnlock] = useState(0);
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [billingSaveSuccess, setBillingSaveSuccess] = useState(false);
  const [transcriptRetentionDays, setTranscriptRetentionDays] = useState(null);
  const [savedTranscriptRetentionDays, setSavedTranscriptRetentionDays] = useState(null);
  const [retentionConfigured, setRetentionConfigured] = useState(false);
  const [isSavingRetention, setIsSavingRetention] = useState(false);
  const [retentionSaveSuccess, setRetentionSaveSuccess] = useState(false);
  const [retentionConfirmOpen, setRetentionConfirmOpen] = useState(false);
  const [retentionPreview, setRetentionPreview] = useState(null);
  const [isLoadingRetentionPreview, setIsLoadingRetentionPreview] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [isLoadingHealth, setIsLoadingHealth] = useState(false);
  const [healthError, setHealthError] = useState(null);
  const [failedLogins24h, setFailedLogins24h] = useState(null);

  // Fetch health data from /health endpoint + audit log metrics
  const fetchHealth = async () => {
    setIsLoadingHealth(true);
    setHealthError(null);
    try {
      const response = await fetch('/health');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setHealthData(data);
    } catch (err) {
      console.error('Error fetching health:', err);
      setHealthError('Unable to reach server');
    }

    // Fetch failed logins (last 24h) from audit logs
    try {
      const oneDayAgo = Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
      const q = query(
        collection(db, 'auditLogs'),
        where('action', '==', 'auth.login.failure'),
        where('timestamp', '>=', oneDayAgo),
      );
      const snapshot = await getCountFromServer(q);
      setFailedLogins24h(snapshot.data().count);
    } catch (err) {
      console.error('Error fetching failed logins:', err);
      setFailedLogins24h(null);
    }

    setIsLoadingHealth(false);
  };

  // Auto-fetch health on mount (General tab)
  useEffect(() => {
    if (activeSubTab === 0) fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // Load system config on mount
  useEffect(() => {
    const loadSystemConfig = async () => {
      try {
        const configDoc = await getDoc(doc(db, 'globalSettings', 'systemConfig'));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data.featureFlags) {
            setFeatureFlags(prev => ({ ...prev, ...data.featureFlags }));
            setSavedFeatureFlags(prev => ({ ...prev, ...data.featureFlags }));
          }
          setGracePeriodDays(data.gracePeriodDays ?? 7);
          setDaysToUnlock(data.daysToUnlock ?? 0);
          const configuredDays = data.retentionPolicy?.transcripts?.retentionDays;
          const normalizedDays = configuredDays === null ? null : Number(configuredDays);
          if (configuredDays !== undefined
            && (normalizedDays === null || TRANSCRIPT_RETENTION_OPTIONS.includes(normalizedDays))) {
            setTranscriptRetentionDays(normalizedDays);
            setSavedTranscriptRetentionDays(normalizedDays);
            setRetentionConfigured(true);
          }
        }
      } catch (err) {
        console.error('Error loading system config:', err);
        setError('Failed to load system configuration');
      }
      setIsLoadingFlags(false);
    };
    loadSystemConfig();
  }, []);

  // Clear cache
  const handleClearCache = async () => {
    try {
      const response = await fetch('/api/v3/configuration/cache/clear', { method: 'POST', headers: await getAuthHeaders() });
      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError('Failed to clear cache');
    }
  };

  // Toggle a feature flag
  const handleFlagChange = (flagName) => (e) => {
    setFeatureFlags(prev => ({ ...prev, [flagName]: e.target.checked }));
  };

  // Compute the pending flag diff vs. what's currently saved. Feature-flag
  // changes cascade to every user in every company immediately, so saving is a
  // deliberate two-step action: review the diff in a dialog, then confirm.
  const flagsBeingEnabled = systemGatedFlags.filter(
    (f) => savedFeatureFlags[f.key] !== true && featureFlags[f.key] === true,
  );
  const flagsBeingDisabled = systemGatedFlags.filter(
    (f) => savedFeatureFlags[f.key] === true && featureFlags[f.key] !== true,
  );
  const flagChangeCount = flagsBeingEnabled.length + flagsBeingDisabled.length;

  // Save feature flags to Firestore
  const handleSaveFlags = async () => {
    // No-op if nothing changed.
    if (flagChangeCount === 0) return;
    // Step 1: open the review dialog. Step 2 (in-dialog) calls back here.
    if (!confirmSaveOpen) {
      setConfirmSaveOpen(true);
      return;
    }
    setConfirmSaveOpen(false);
    setIsSavingFlags(true);
    setError(null);
    try {
      await setDoc(doc(db, 'globalSettings', 'systemConfig'), {
        featureFlags,
        updatedAt: new Date(),
        updatedBy: currentUser?.email || 'unknown',
      }, { merge: true });
      setSavedFeatureFlags({ ...featureFlags });
      setFlagSaveSuccess(true);
      setTimeout(() => setFlagSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving feature flags:', err);
      setError('Failed to save feature flags');
    }
    setIsSavingFlags(false);
  };

  // Save billing settings to Firestore
  const handleSaveBilling = async () => {
    if (!billingPolicy.enabled) return;
    setIsSavingBilling(true);
    setError(null);
    try {
      await setDoc(doc(db, 'globalSettings', 'systemConfig'), {
        gracePeriodDays,
        daysToUnlock,
        updatedAt: new Date(),
        updatedBy: currentUser?.email || 'unknown',
      }, { merge: true });
      setBillingSaveSuccess(true);
      setTimeout(() => setBillingSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving billing settings:', err);
      setError('Failed to save billing settings');
    }
    setIsSavingBilling(false);
  };

  const retentionHasChanges = !retentionConfigured
    ? transcriptRetentionDays !== null
    : transcriptRetentionDays !== savedTranscriptRetentionDays;

  const handlePreviewRetention = async () => {
    setIsLoadingRetentionPreview(true);
    setRetentionPreview(null);
    setError(null);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');
      const previewValue = transcriptRetentionDays === null ? 'off' : transcriptRetentionDays;
      const response = await fetch(`/api/v3/admin/transcript-retention/preview?retentionDays=${previewValue}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Preview failed (${response.status})`);
      setRetentionPreview(await response.json());
    } catch (err) {
      console.error('Error previewing transcript retention:', err);
      setError('Failed to preview transcript retention candidates');
    }
    setIsLoadingRetentionPreview(false);
  };

  const handleSaveRetention = async () => {
    if (!retentionHasChanges) return;
    if (!retentionConfirmOpen) {
      setRetentionConfirmOpen(true);
      return;
    }

    setRetentionConfirmOpen(false);
    setIsSavingRetention(true);
    setError(null);
    try {
      const configRef = doc(db, 'globalSettings', 'systemConfig');
      const auditRef = doc(collection(db, 'auditLogs'));
      await runTransaction(db, async (transaction) => {
        const configSnapshot = await transaction.get(configRef);
        const existing = configSnapshot.exists() ? configSnapshot.data() : {};
        transaction.set(configRef, {
          retentionPolicy: {
            ...(existing.retentionPolicy || {}),
            transcripts: {
              retentionDays: transcriptRetentionDays,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser?.uid || 'unknown',
            },
          },
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || 'unknown',
        }, { merge: true });
        transaction.set(auditRef, {
          action: 'retention.transcript_policy_change',
          userId: currentUser?.uid,
          performedByEmail: currentUser?.email || 'unknown',
          resourceType: 'systemConfig',
          resourceId: 'transcriptRetention',
          previousValue: retentionConfigured ? savedTranscriptRetentionDays : null,
          newValue: transcriptRetentionDays,
          timestamp: serverTimestamp(),
        });
      });

      setSavedTranscriptRetentionDays(transcriptRetentionDays);
      setRetentionConfigured(true);
      setRetentionPreview(null);
      setRetentionSaveSuccess(true);
      setTimeout(() => setRetentionSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving transcript retention:', err);
      setError('Failed to save transcript retention policy');
    }
    setIsSavingRetention(false);
  };

  // Clamp a typed numeric value into the knob's bounds.
  const clampToBounds = (item, raw) => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return item.min;
    return Math.max(item.min, Math.min(item.max, n));
  };

  // Render the right control for a setting (slider+number / number / range).
  // Generic over value + onChange so persisted settings share one style.
  const renderControl = (item, value, onChange) => {
    if (item.control === 'number') {
      return (
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={(e) => onChange(clampToBounds(item, e.target.value))}
          inputProps={{ min: item.min, max: item.max, step: item.step }}
          sx={{ width: 110 }}
        />
      );
    }

    if (item.control === 'range') {
      return (
        <Box>
          <Slider
            size="small"
            value={value}
            onChange={(e, v) => onChange(v)}
            min={item.min}
            max={item.max}
            step={item.step}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}${item.unit}`}
            disableSwap
          />
          <Typography variant="caption" color="text.secondary">
            {`${value[0]}${item.unit} → ${value[1]}${item.unit}`}
          </Typography>
        </Box>
      );
    }

    // 'slider' — slider + number combo
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Slider
          size="small"
          value={value}
          onChange={(e, v) => onChange(v)}
          min={item.min}
          max={item.max}
          step={item.step}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}${item.unit}`}
          sx={{ flex: 1 }}
        />
        <TextField
          type="number"
          size="small"
          value={value}
          onChange={(e) => onChange(clampToBounds(item, e.target.value))}
          inputProps={{ min: item.min, max: item.max, step: item.step }}
          sx={{ width: 90 }}
        />
      </Box>
    );
  };

  // Render a full setting row: label + description + control.
  // 'number' controls sit on a single row (label left, input right);
  // sliders stack the control full-width beneath the label.
  const renderSettingRow = (item, value, onChange) => {
    if (item.control === 'number') {
      const rangeText = `${item.min}–${item.max}${item.unit ? ` ${item.unit}` : ''}`;
      return (
        <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.25 }}>
              {item.description} ({rangeText})
            </Typography>
          </Box>
          {renderControl(item, value, onChange)}
        </Box>
      );
    }

    return (
      <Box key={item.key}>
        <Typography variant="body2" color="text.secondary">
          {item.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.25, mb: 1 }}>
          {item.description}
        </Typography>
        {renderControl(item, value, onChange)}
      </Box>
    );
  };

  // Bridge the registry keys to the existing billing state/setters.
  const billingValues = { gracePeriodDays, daysToUnlock };
  const billingSetters = { gracePeriodDays: setGracePeriodDays, daysToUnlock: setDaysToUnlock };

  return (
    <Box sx={{ p: 2, maxWidth: 600 }}>
      <Typography variant="h5" gutterBottom>
        System Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cache cleared successfully.
        </Alert>
      )}

      {flagSaveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Feature flags saved successfully.
        </Alert>
      )}

      {billingSaveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Billing settings saved successfully.
        </Alert>
      )}

      {retentionSaveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Transcript retention policy saved successfully.
        </Alert>
      )}

      <Tabs
        value={activeSubTab}
        onChange={(e, newValue) => {
          if (hasUnsavedTemplateChanges && !window.confirm('You have unsaved changes. Discard them?')) return;
          setActiveSubTab(newValue);
          if (userId && setAdminPrefs) {
            const updatedPrefs = { ...adminPrefs, systemSubTab: newValue };
            setAdminPrefs(updatedPrefs);
            updateUserSettings(userId, { adminPreferences: updatedPrefs });
          }
        }}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="General" />
        <Tab label="Features" />
        <Tab label="Templates" />
        <Tab label="Doc Type Templates" />
      </Tabs>

      {/* General subtab */}
      {activeSubTab === 0 && (
        <>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                System Health
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchHealth}
                disabled={isLoadingHealth}
              >
                Refresh
              </Button>
            </Box>

            {healthError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {healthError}
              </Alert>
            )}

            {isLoadingHealth && !healthData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : healthData ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Environment</Typography>
                  <Typography variant="body2" fontWeight={500}>{getEnvironmentLabel()}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Version</Typography>
                  <Typography variant="body2" fontWeight={500}>v{versionNum}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Failed logins (24h)</Typography>
                  {failedLogins24h !== null ? (
                    <Chip
                      label={failedLogins24h}
                      size="small"
                      color={failedLogins24h > 10 ? 'error' : failedLogins24h > 0 ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </Box>
              </Box>
            ) : null}

            <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Firestore cache (5&nbsp;min TTL)
              </Typography>
              <Button size="small" variant="outlined" onClick={handleClearCache}>
                Clear Cache
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Retention
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete source transcripts after the selected period. Finished clinical documents are retained. Company Litigation Holds override this policy.
            </Typography>

            {!retentionConfigured && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Transcript cleanup is currently off because no policy has been saved.
              </Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Transcript retention</InputLabel>
              <Select
                value={transcriptRetentionDays ?? 'off'}
                label="Transcript retention"
                onChange={(event) => {
                  const value = event.target.value;
                  setTranscriptRetentionDays(value === 'off' ? null : Number(value));
                  setRetentionPreview(null);
                }}
              >
                {TRANSCRIPT_RETENTION_OPTIONS.map((days) => (
                  <MenuItem key={days} value={days}>{days} days</MenuItem>
                ))}
                <MenuItem value="off">Off — retain indefinitely</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handlePreviewRetention}
                disabled={isLoadingRetentionPreview}
              >
                {isLoadingRetentionPreview ? 'Checking…' : 'Preview affected transcripts'}
              </Button>
              <Button
                variant="contained"
                color={transcriptRetentionDays === null ? 'primary' : 'error'}
                onClick={handleSaveRetention}
                disabled={
                  isSavingRetention
                  || !retentionHasChanges
                  || (transcriptRetentionDays !== null
                    && retentionPreview?.retentionDays !== transcriptRetentionDays)
                }
              >
                {isSavingRetention ? 'Saving…' : 'Review & Save…'}
              </Button>
            </Box>

            {retentionHasChanges && transcriptRetentionDays !== null
              && retentionPreview?.retentionDays !== transcriptRetentionDays && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Preview the selected retention window before saving this destructive policy.
              </Typography>
            )}

            {retentionPreview && (
              <Alert
                severity={retentionPreview.enabled ? 'warning' : 'info'}
                sx={{ mt: 2 }}
              >
                {retentionPreview.enabled ? (
                  <>
                    <strong>{retentionPreview.eligible}</strong> transcript{retentionPreview.eligible === 1 ? '' : 's'} currently eligible;{' '}
                    <strong>{retentionPreview.held}</strong> protected by Litigation Hold;{' '}
                    <strong>{retentionPreview.failed + retentionPreview.missingCompany + retentionPreview.invalidPointer + retentionPreview.missingTimestamp}</strong> require review.
                    {retentionPreview.truncated ? ' The preview reached its scan limit.' : ''}
                  </>
                ) : (
                  <>Cleanup is off; no transcripts will be deleted.</>
                )}
              </Alert>
            )}
          </CardContent>
        </Card>

        {billingPolicy.enabled && <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Billing Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Grace periods and trial settings for subscription billing.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {BILLING_SETTINGS.map((item) =>
                renderSettingRow(
                  item,
                  billingValues[item.key],
                  (v) => billingSetters[item.key](v),
                ),
              )}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSaveBilling}
                disabled={isSavingBilling}
              >
                {isSavingBilling ? 'Saving...' : 'Save Billing Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>}
        </>
      )}

      <Dialog
        open={retentionConfirmOpen}
        onClose={() => setRetentionConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Apply transcript retention policy?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {transcriptRetentionDays === null
              ? 'Automatic transcript deletion will be turned off. Existing transcripts will be retained indefinitely unless this policy is enabled again.'
              : `Source transcripts older than ${transcriptRetentionDays} days will be permanently deleted with no recovery period. The policy applies to existing and new transcripts; finished clinical documents remain available.`}
          </DialogContentText>
          {transcriptRetentionDays !== null && retentionPreview && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              The current preview found {retentionPreview.eligible} eligible transcript{retentionPreview.eligible === 1 ? '' : 's'} and {retentionPreview.held} protected by Litigation Hold.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetentionConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={transcriptRetentionDays === null ? 'primary' : 'error'}
            onClick={handleSaveRetention}
            disabled={isSavingRetention}
          >
            {transcriptRetentionDays === null ? 'Turn off cleanup' : 'Apply policy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feature Flags subtab */}
      {activeSubTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Master switches for platform features. When a flag is OFF here,
              the feature is disabled for all users regardless of company or user settings.
            </Typography>

            {isLoadingFlags ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {systemGatedFlags.map((flag) => (
                  <React.Fragment key={flag.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={featureFlags[flag.key] || false}
                          onChange={handleFlagChange(flag.key)}
                        />
                      }
                      label={flag.label}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: '48px', mt: -1, fontSize: '0.75rem' }}
                    >
                      {flag.description}
                    </Typography>
                  </React.Fragment>
                ))}

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    color={flagsBeingDisabled.length > 0 ? 'error' : 'primary'}
                    onClick={handleSaveFlags}
                    disabled={isSavingFlags || flagChangeCount === 0}
                  >
                    {isSavingFlags ? 'Saving...' : 'Review & Save…'}
                  </Button>
                  {flagChangeCount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {flagChangeCount} pending change{flagChangeCount === 1 ? '' : 's'}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2 — review the full flag diff before applying. */}
      <Dialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: flagsBeingDisabled.length > 0 ? 'error.main' : 'text.primary' }}>
          <WarningAmberIcon color={flagsBeingDisabled.length > 0 ? 'error' : 'warning'} />
          Apply feature flag changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            These changes take effect for <strong>all users across all companies</strong> immediately
            — including yourself. There is no per-user rollback. Review carefully before applying.
          </DialogContentText>

          {flagsBeingEnabled.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 600, mb: 0.5 }}>
                Enabling ({flagsBeingEnabled.length})
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {flagsBeingEnabled.map((f) => (
                  <li key={f.key}>{f.label}</li>
                ))}
              </Box>
            </Box>
          )}

          {flagsBeingDisabled.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 600, mb: 0.5 }}>
                Disabling ({flagsBeingDisabled.length}) — turns the feature off everywhere
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, color: 'error.main', fontWeight: 600 }}>
                {flagsBeingDisabled.map((f) => (
                  <li key={f.key}>{f.label}</li>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSaveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={flagsBeingDisabled.length > 0 ? 'error' : 'primary'}
            onClick={handleSaveFlags}
            disabled={isSavingFlags}
          >
            {isSavingFlags ? 'Saving...' : 'Apply changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Templates subtab */}
      {activeSubTab === 2 && <AdminTemplates onDirtyChange={setHasUnsavedTemplateChanges} />}

      {/* Doc Type Templates subtab */}
      {activeSubTab === 3 && <AdminDocumentTypeTemplates onDirtyChange={setHasUnsavedTemplateChanges} />}
    </Box>
  );
};

export default AdminSystemSettings;
